import { useState } from "react"
import toast from "react-hot-toast"

import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@aix/ui"

import { trpc } from "../lib/trpc"
import SEOProductSettings from "./SEOProductSettings"

interface SEOPublishTabProps {
  productKey: string
}

interface CompileResult {
  dryRun: boolean
  manifest: { version: number; generatedAt: string; pageCount: number }
  clusterCounts: Record<string, number>
  uploadedKeys: string[]
  published: number
}

export default function SEOPublishTab({ productKey }: SEOPublishTabProps) {
  const [result, setResult] = useState<CompileResult | null>(null)
  const utils = trpc.useUtils()

  const compileMutation = trpc.seo.publish.compile.useMutation({
    onError: (e) => toast.error(e.message || "Compile failed"),
    onSuccess: (res) => {
      setResult(res)
      toast.success(`Previewed ${res.manifest.pageCount} gated or published page(s)`)
      void utils.seo.pages.list.invalidate()
    },
  })

  const clusterEntries = result ? Object.entries(result.clusterCounts) : []

  return (
    <div className="h-full overflow-auto p-4">
      <div className="mx-auto max-w-3xl space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Legacy compile preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This read-only preview counts the older cluster bundle. New production delivery uses a proposed, immutable
              release shown in Operations: AIX compiles complete HTML and JSON, a human approves it, and only then can
              the current-release pointer change.
            </p>
            <Button
              variant="outline"
              onClick={() => compileMutation.mutate({ productKey, dryRun: true })}
              loading={compileMutation.isLoading}
            >
              Preview gated inventory
            </Button>
          </CardContent>
        </Card>

        {result ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Preview result
                <Badge variant="secondary">not a release</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-6 text-sm">
                <span>
                  <span className="text-muted-foreground">Manifest version: </span>v{result.manifest.version}
                </span>
                <span>
                  <span className="text-muted-foreground">Total pages: </span>
                  {result.manifest.pageCount}
                </span>
                <span>
                  <span className="text-muted-foreground">Published by this action: </span>
                  {result.published}
                </span>
                <span>
                  <span className="text-muted-foreground">Generated: </span>
                  {new Date(result.manifest.generatedAt).toLocaleString()}
                </span>
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Per-cluster counts</p>
                {clusterEntries.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No gated/published pages to compile. Gate some pages first.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="py-1">Cluster</th>
                        <th className="py-1">Pages</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clusterEntries.map(([cluster, count]) => (
                        <tr key={cluster} className="border-t border-border">
                          <td className="py-1">{cluster}</td>
                          <td className="py-1">{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {result.uploadedKeys.length ? (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Uploaded R2 keys</p>
                  <ul className="space-y-0.5 font-mono text-xs text-muted-foreground">
                    {result.uploadedKeys.map((k) => (
                      <li key={k}>{k}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        <SEOProductSettings productKey={productKey} />
      </div>
    </div>
  )
}
